import { EventDeclarations } from '../event-store/event-declarations.js';
import { StateValue } from '../storage/state-value.js';
import { validate } from '../typing/validate.js';
import {
  deserializeData,
  getStateData,
} from '../runtime/get-state-data-keys.js';
import { Telemetry } from '../tracer/telemetry.js';

export interface EventTransformer<TEvents extends EventDeclarations> {
  serialize<K extends string & keyof TEvents>(
    pattern: string,
    evt: K,
    data: TEvents[K]['type']['_output'],
  ): Record<string, StateValue>;
  deserialize<K extends string & keyof TEvents>(
    pattern: string,
    evt: K,
    data: Record<string, StateValue>,
  ): TEvents[K]['type']['_output'];
}

export function createEventStoreTransformer<
  TEvents extends EventDeclarations,
>(opts: {
  patterns: Record<string, EventDeclarations>;
  telemetry: Telemetry;
}): EventTransformer<TEvents> {
  const transformer = opts.telemetry.createTracer('event-store-transformer');

  return {
    deserialize<K extends string & keyof TEvents>(
      pattern: string,
      evt: K,
      data: Record<string, StateValue>,
    ): TEvents[K]['type']['_output'] {
      const events = opts.patterns[pattern];
      if (!events) {
        throw new Error(`pattern ${pattern} not found`);
      }

      const eventStruct = events[evt];
      if (!eventStruct) {
        throw new Error(`event type ${evt} not found`);
      }

      return transformer.createSpan(
        'deserialize',
        { attributes: { event: evt, pattern } },
        () => {
          return deserializeData(eventStruct.type, data, opts.telemetry);
        },
      );
    },
    serialize<K extends string & keyof TEvents>(
      pattern: string,
      evt: K,
      data: TEvents[K]['type']['_output'],
    ): Record<string, StateValue> {
      const events = opts.patterns[pattern];
      if (!events) {
        throw new Error(`pattern ${pattern} not found`);
      }

      const eventStruct = events[evt];
      if (!eventStruct) {
        throw new Error(`event type ${evt} not found`);
      }

      const [eventError, eventData] = validate(data, eventStruct.type);
      if (eventError) {
        throw new Error('event validation error: ' + eventError.message);
      }

      return transformer.createSpan(
        'serialize',
        { attributes: { event: evt, pattern } },
        () => {
          return getStateData(eventStruct.type, eventData);
        },
      );
    },
  };
}
